import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Check, ArrowRight, Volume2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Robot from '@/components/Robot';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useRobotVoice } from '@/hooks/useRobotVoice';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useApp } from '@/context/AppContext';
import { DEFAULT_UI_TEXTS } from '@/services/dataRepo';
import { toast } from 'sonner';
import { glowPointerHandlers } from '@/lib/glowPointer';
import { choiceLabel, choiceHasVoiceFollowup, choiceFollowupRobotText } from '@/lib/questionChoices';
import { playSelectionSound } from '@/lib/playSelectionSound';
import { robotTextOrDefault, robotTextOptional, replaceNamePlaceholders } from '@/lib/robotText';
import {
  primeSpeechSynthesisFromUserInteraction,
  scheduleSpeechEngagedOrFallback,
  speakTextFromUserGesture,
} from '@/lib/nativeTts';

const choiceHoverHalo =
  'shadow-none hover:[box-shadow:inset_0_0_0_2px_rgba(255,255,255,0.95),0_0_16px_rgba(255,255,255,0.88),0_0_34px_rgba(255,255,255,0.45),0_0_52px_rgba(255,255,255,0.2)] transition-[box-shadow] duration-200 ease-out';

const choiceRadialClasses = [
  'btn-choice-radial-0',
  'btn-choice-radial-1',
  'btn-choice-radial-2',
  'btn-choice-radial-3',
  'btn-choice-radial-4',
  'btn-choice-radial-5'
];

const HOVER_DELAY = 500; // 0.5 seconds

/** Après la fin de l’animation d’intro : léger délai pour que le robot soit bien affiché avant la synthèse. */
const INTRO_POST_MOTION_MS = 220;
const QUESTION_PANEL_ENTRANCE_MS = 750;

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const QuestionnairePage = () => {
  const navigate = useNavigate();
  const { speak, isSpeaking, speechSpectrum, stop } = useRobotVoice();
  const { isRecording, audioUrl, startRecording, stopRecording, getBase64Audio, resetRecording } = useAudioRecorder();
  const { questions, fetchQuestions, session, setSession, createSession, updateSessionNameAudio, addResponse, uploadAudio, completeSession, settings, fetchSettings } = useApp();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [audioRecorded, setAudioRecorded] = useState(false);
  const [showAudioStep, setShowAudioStep] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadingError, setLoadingError] = useState('');
  const [hasReadAllChoices, setHasReadAllChoices] = useState(false);
  const [isReadingChoices, setIsReadingChoices] = useState(false);
  const [currentReadingIndex, setCurrentReadingIndex] = useState(-1); // Which choice is being read
  const [hoverIndex, setHoverIndex] = useState(-1); // Which choice is being hovered
  const [hoverProgress, setHoverProgress] = useState(0); // Timer progress 0-100
  
  // Identity question states
  const [childName, setChildName] = useState('');
  const [identityStep, setIdentityStep] = useState(1); // 1: name input, 2: voice recording
  const [showIntro, setShowIntro] = useState(true);
  const [hasSpokenIntro, setHasSpokenIntro] = useState(false);
  /** True quand l’animation d’affichage du bloc « Introduction » est terminée (pas pendant le chargement / le fondu). */
  const [introMotionComplete, setIntroMotionComplete] = useState(false);
  const introSpeakStartedRef = useRef(false);
  const introSpeakGenRef = useRef(0);
  /** Flash vert court après envoi du prénom (identité étape 1). */
  const [nameSubmitFlash, setNameSubmitFlash] = useState(false);
  const hasSpokenRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const hoverStartRef = useRef(null);
  const animationFrameRef = useRef(null);

  /**
   * Ancre TTS / reset d’écran : index + id de question uniquement.
   * Évite de tout réinitialiser (choix QCM, étape micro) quand le prof modifie le libellé ou que `speak` change de référence.
   */
  const questionTtsAnchorKey = useMemo(() => {
    const q = questions[currentIndex];
    if (!q) return `i${currentIndex}:none`;
    return `i${currentIndex}:id:${q.id}`;
  }, [questions, currentIndex]);

  const speakRef = useRef(speak);
  speakRef.current = speak;
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const resetRecordingRef = useRef(resetRecording);
  resetRecordingRef.current = resetRecording;

  /** Choix QCM retenu avant l’étape micro (ne pas perdre si l’état React est réinitialisé par erreur). */
  const mcVoiceFollowupChoiceRef = useRef(null);
  /** Évite double envoi (double clic / courses async). */
  const responseSaveLockRef = useRef(false);

  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  useEffect(() => {
    if (!settings) void fetchSettings();
  }, [settings, fetchSettings]);

  useEffect(() => {
    const prime = () => {
      primeSpeechSynthesisFromUserInteraction();
    };
    window.addEventListener('pointerdown', prime, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const data = await fetchQuestions();
      if (data === null) {
        setLoadingError(
          "Impossible de récupérer les questions (connexion ou serveur). Réessaie dans un instant, ou passe sur le Wi‑Fi. Si ça marche sur un ordinateur mais pas ici, le réseau mobile bloque peut‑être l’accès au site."
        );
      } else if (!data.length) {
        setLoadingError(
          "Impossible de charger les questions. Vérifie la configuration NocodeBackend (.env) et recharge la page."
        );
      } else {
        setLoadingError('');
      }
    };
    load();
  }, [fetchQuestions]);

  useEffect(() => {
    setIntroMotionComplete(false);
    introSpeakStartedRef.current = false;
  }, [showIntro]);

  useEffect(() => {
    if (!showIntro || hasSpokenIntro || !introMotionComplete) return undefined;
    if (introSpeakStartedRef.current) return undefined;

    const introVoice = robotTextOrDefault(settings, 'question_intro');

    let cancelled = false;
    let introReplayWatch = null;
    const gen = ++introSpeakGenRef.current;

    const replayOnGesture = () => {
      if (!showIntro) return;
      primeSpeechSynthesisFromUserInteraction();
      if (introReplayWatch) {
        clearTimeout(introReplayWatch);
        introReplayWatch = null;
      }
      if (!speakTextFromUserGesture(introVoice)) {
        void speak(introVoice);
        return;
      }
      introReplayWatch = scheduleSpeechEngagedOrFallback({
        onEngaged: () => {
          introReplayWatch = null;
        },
        onSilent: () => {
          introReplayWatch = null;
          void speak(introVoice);
        },
      });
    };

    const run = async () => {
      introSpeakStartedRef.current = true;
      await new Promise((r) => setTimeout(r, INTRO_POST_MOTION_MS));
      if (cancelled || gen !== introSpeakGenRef.current) return;
      speak(introVoice);
      setHasSpokenIntro(true);
      window.addEventListener('pointerdown', replayOnGesture, { once: true });
      window.addEventListener('keydown', replayOnGesture, { once: true });
    };

    void run();

    return () => {
      cancelled = true;
      introSpeakGenRef.current += 1;
      introSpeakStartedRef.current = false;
      if (introReplayWatch) {
        clearTimeout(introReplayWatch);
        introReplayWatch = null;
      }
      window.removeEventListener('pointerdown', replayOnGesture);
      window.removeEventListener('keydown', replayOnGesture);
    };
  }, [showIntro, hasSpokenIntro, introMotionComplete, settings, speak]);

  // Identité — étape micro : texte paramétrable (réglages) ; vide = pas de phrase avant le micro.
  useEffect(() => {
    const list = questionsRef.current;
    if (showIntro || isTransitioning || !list.length) return;
    const q = list[currentIndex];
    if (q?.type !== 'identity' || identityStep !== 2) return;
    let cancelled = false;
    const run = async () => {
      setHasReadAllChoices(false);
      const msg = robotTextOptional(settings, 'ask_name_audio');
      if (msg) await speakRef.current(msg);
      if (!cancelled) setHasReadAllChoices(true);
    };
    run();
    return () => {
      cancelled = true;
      stopRef.current();
    };
  }, [
    currentIndex,
    identityStep,
    questionTtsAnchorKey,
    showIntro,
    isTransitioning,
    settings,
  ]);

  // Read question and all choices with highlighting
  useEffect(() => {
    const list = questionsRef.current;
    if (!showIntro && list.length > 0 && currentIndex < list.length && !isTransitioning) {
      const question = list[currentIndex];
      hasSpokenRef.current = false;
      setHasReadAllChoices(false);
      setSelectedChoice(null);
      mcVoiceFollowupChoiceRef.current = null;
      setAudioRecorded(false);
      setShowAudioStep(false);
      setCurrentReadingIndex(-1);
      resetRecordingRef.current();
      
      setIdentityStep(1);
      setChildName('');
      setNameSubmitFlash(false);
      
      let cancelled = false;

      const readQuestionAndChoices = async () => {
        if (hasSpokenRef.current) return;
        hasSpokenRef.current = true;

        await new Promise((r) => setTimeout(r, QUESTION_PANEL_ENTRANCE_MS));
        if (cancelled) {
          hasSpokenRef.current = false;
          return;
        }

        const isMulti = question.type === 'multiple_choice' && question.choices?.length > 0;

        // Bloquer tout de suite le survol / clic sur les choix (y compris pendant l'énoncé de la question).
        if (isMulti) {
          setIsReadingChoices(true);
        }

        await speakRef.current(question.robot_text);
        if (cancelled) return;

        if (isMulti) {
          for (let i = 0; i < question.choices.length; i++) {
            if (cancelled) break;
            setCurrentReadingIndex(i);
            await speakRef.current(choiceLabel(question.choices[i]));
          }

          setCurrentReadingIndex(-1);
          setIsReadingChoices(false);
        }

        if (!cancelled) setHasReadAllChoices(true);
        else {
          setCurrentReadingIndex(-1);
          setIsReadingChoices(false);
        }
      };
      
      readQuestionAndChoices();

      return () => {
        cancelled = true;
        stopRef.current();
      };
    }
    return undefined;
  }, [questionTtsAnchorKey, isTransitioning, showIntro, currentIndex]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const introText = settings?.ui_texts?.intro_message
    || "Je vais te poser quelques questions. Il n'y a pas de bonne ou mauvaise réponse.";
  const introTitle = settings?.ui_texts?.intro_title || 'Introduction';

  const isMultiChoiceView =
    currentQuestion && currentQuestion.type === 'multiple_choice' && !showAudioStep;
  const choicesInteractionLocked =
    Boolean(isMultiChoiceView) && (!hasReadAllChoices || isReadingChoices || selectedChoice != null);

  const mcFollowupDisplayText =
    showAudioStep && currentQuestion?.type === 'multiple_choice'
      ? choiceFollowupRobotText(currentQuestion, selectedChoice)
      : '';

  // Handle hover start with timer
  const handleHoverStart = (index, choice) => {
    if (!hasReadAllChoices || isReadingChoices || selectedChoice != null) return;
    
    setHoverIndex(index);
    setHoverProgress(0);
    hoverStartRef.current = Date.now();
    
    // Animate progress
    const animateProgress = () => {
      const elapsed = Date.now() - hoverStartRef.current;
      const progressPercent = Math.min((elapsed / HOVER_DELAY) * 100, 100);
      setHoverProgress(progressPercent);
      
      if (progressPercent < 100) {
        animationFrameRef.current = requestAnimationFrame(animateProgress);
      }
    };
    animationFrameRef.current = requestAnimationFrame(animateProgress);
    
    // Set timer to speak
    hoverTimerRef.current = setTimeout(() => {
      stop();
      setCurrentReadingIndex(index);
      speak(choice).then(() => setCurrentReadingIndex(-1));
    }, HOVER_DELAY);
  };

  // Handle hover end
  const handleHoverEnd = () => {
    setHoverIndex(-1);
    setHoverProgress(0);
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Speak choice on click (for mobile)
  const handleSpeakChoice = (e, index, choice) => {
    e.stopPropagation();
    if (!hasReadAllChoices || isReadingChoices || selectedChoice != null) return;
    stop();
    setCurrentReadingIndex(index);
    speak(choice).then(() => setCurrentReadingIndex(-1));
  };

  const handleChoiceSelect = async (choice) => {
    if (!hasReadAllChoices || isReadingChoices) return;
    if (showAudioStep || isTransitioning || responseSaveLockRef.current) return;

    handleHoverEnd(); // Clear any pending hover
    playSelectionSound(); // Play selection sound
    setSelectedChoice(choice);
    
    if (!choiceHasVoiceFollowup(currentQuestion, choice)) {
      mcVoiceFollowupChoiceRef.current = null;
      await saveAndContinue(choice);
      return;
    }
    mcVoiceFollowupChoiceRef.current = choice;
    setHasReadAllChoices(false);
    setShowAudioStep(true);
    const followupSpoken = choiceFollowupRobotText(currentQuestion, choice);
    if (followupSpoken) await speakRef.current(followupSpoken);
    setHasReadAllChoices(true);
  };

  const handleRecordToggle = async () => {
    if (!isRecording && !hasReadAllChoices) return;

    if (isRecording) {
      const blob = await stopRecording();
      const ok = Boolean(blob && blob.size > 0);
      setAudioRecorded(ok);
      if (!ok) {
        toast.error("Enregistrement trop court, réessaie.");
        return;
      }
      if (isTransitioning || responseSaveLockRef.current) return;
      const dataUrl = await blobToDataURL(blob);

      const stopValidatesSend =
        (currentQuestion.type === 'open_voice' && !showAudioStep) ||
        (currentQuestion.type === 'identity' && identityStep === 2) ||
        (showAudioStep && currentQuestion.type === 'multiple_choice');
      if (stopValidatesSend) playSelectionSound();

      // Second click on red (stop) also validates — no extra send button.
      if (currentQuestion.type === 'open_voice' && !showAudioStep) {
        await saveAndContinue(null, false, { forcedAudioDataUrl: dataUrl });
        return;
      }
      if (currentQuestion.type === 'identity' && identityStep === 2) {
        await handleIdentityVoiceContinue(dataUrl);
        return;
      }
      if (showAudioStep && currentQuestion.type === 'multiple_choice') {
        await saveAndContinue(selectedChoice, false, { forcedAudioDataUrl: dataUrl });
        return;
      }
    } else {
      resetRecording();
      setAudioRecorded(false);
      try {
        await startRecording();
      } catch (err) {
        toast.error("Je n'arrive pas à entendre ta voix.");
      }
    }
  };

  // === IDENTITY QUESTION HANDLERS ===
  const handleNameSubmit = async () => {
    if (!hasReadAllChoices) return;
    if (!childName.trim()) {
      toast.error("Écris ton prénom !");
      return;
    }

    // Create session with child name
    const newSession = await createSession(childName.trim());
    if (!newSession) {
      toast.error("Oups ! Réessaye.");
      return;
    }

    playSelectionSound();

    const thankText = (currentQuestion.thank_text || '').trim();
    if (thankText) await speak(replaceNamePlaceholders(thankText, childName.trim()));
    setNameSubmitFlash(true);
    window.setTimeout(() => setNameSubmitFlash(false), 2200);

    // Identity questions always include the optional voice step.
    if (currentQuestion.type === 'identity') {
      setHasReadAllChoices(false);
      setIdentityStep(2);
    } else {
      // Move to next question
      moveToNextQuestion();
    }
  };

  const handleIdentityVoiceContinue = async (forcedAudioDataUrl = null) => {
    let audioPayload = forcedAudioDataUrl;
    if (!audioPayload && audioRecorded && audioUrl && session) {
      audioPayload = await getBase64Audio();
    }
    if (audioPayload && session) {
      const uploadedUrl = await uploadAudio(audioPayload, session.id, 'name');
      if (uploadedUrl) {
        await updateSessionNameAudio(session.id, uploadedUrl);
      }
    }

    const done = robotTextOrDefault(settings, 'identity_voice_done');
    if (done) await speak(done);
    
    setTimeout(() => {
      moveToNextQuestion();
    }, 1500);
  };

  const handleSkipIdentityVoice = async () => {
    if (!hasReadAllChoices) return;
    const skipMsg = robotTextOrDefault(settings, 'ask_name_audio_skip');
    if (skipMsg) await speak(skipMsg);
    setTimeout(() => {
      moveToNextQuestion();
    }, 1500);
  };

  const moveToNextQuestion = () => {
    setIsTransitioning(true);
    stop();
    
    setTimeout(async () => {
      if (currentIndex < questions.length - 1) {
        setIsTransitioning(false);
        setCurrentIndex((prev) => prev + 1);
      } else {
        if (session) await completeSession(session.id);
        navigate('/thank-you');
      }
    }, 500);
  };
  // === END IDENTITY HANDLERS ===

  const saveAndContinue = async (choice = null, skipAudio = false, options = {}) => {
    const { forcedAudioDataUrl = null } = options;
    if (!session || !currentQuestion || isTransitioning) return;
    if (responseSaveLockRef.current) return;
    responseSaveLockRef.current = true;
    setIsTransitioning(true);
    stop();

    const trimOrNull = (v) => {
      if (v == null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    };

    let audioUrlToSave = null;

    try {
      if (!skipAudio) {
        if (forcedAudioDataUrl) {
          audioUrlToSave = await uploadAudio(
            forcedAudioDataUrl,
            session.id,
            `response_${currentQuestion.id}`
          );
        } else if (audioRecorded && audioUrl) {
          const base64Audio = await getBase64Audio();
          if (base64Audio) {
            audioUrlToSave = await uploadAudio(base64Audio, session.id, `response_${currentQuestion.id}`);
          }
        }
      }

      const resolvedChoice =
        trimOrNull(choice)
        ?? trimOrNull(mcVoiceFollowupChoiceRef.current)
        ?? trimOrNull(selectedChoice);

      const skipVoiceNote =
        skipAudio
        && currentQuestion.type === 'multiple_choice'
        && trimOrNull(mcVoiceFollowupChoiceRef.current) != null
          ? (
              (settings?.ui_texts?.mc_voice_skipped_note || '').trim()
              || DEFAULT_UI_TEXTS.mc_voice_skipped_note
            )
          : null;

      const choiceLabels =
        currentQuestion.type === 'multiple_choice'
          ? (currentQuestion.choices || [])
            .map((c) => choiceLabel(c).trim())
            .filter(Boolean)
          : null;

      const response = {
        question_id: currentQuestion.id,
        question_text: currentQuestion.text,
        choice_selected: resolvedChoice,
        text_response: skipVoiceNote,
        audio_url: audioUrlToSave,
        choices_offered: choiceLabels && choiceLabels.length ? choiceLabels : null,
      };

      await addResponse(session.id, response);
    } catch (err) {
      console.error(err);
      toast.error("Oups, la réponse n'a pas pu être enregistrée. Réessaie.");
      responseSaveLockRef.current = false;
      setIsTransitioning(false);
      return;
    }

    mcVoiceFollowupChoiceRef.current = null;

    const thankText = (currentQuestion.thank_text || '').trim();
    if (thankText) {
      try {
        await Promise.race([speakRef.current(thankText), new Promise((r) => setTimeout(r, 2000))]);
      } catch {
        /* TTS indisponible : on continue quand même */
      }
    }

    setTimeout(async () => {
      try {
        if (currentIndex < questions.length - 1) {
          setIsTransitioning(false);
          setCurrentIndex((prev) => prev + 1);
        } else {
          if (session) await completeSession(session.id);
          navigate('/thank-you');
        }
      } finally {
        responseSaveLockRef.current = false;
      }
    }, 1500);
  };

  const handleSkipAudioAndContinue = () => {
    if (!hasReadAllChoices || responseSaveLockRef.current) return;
    saveAndContinue(null, true);
  };

  if (!currentQuestion) {
    return (
      <ThemeWrapper className="flex items-center justify-center min-h-screen">
        <div className="text-center px-6">
          {!loadingError ? (
            <div className="animate-pulse text-2xl text-white">Chargement...</div>
          ) : (
            <>
              <div className="text-xl text-red-300 mb-4">{loadingError}</div>
              <Button onClick={() => window.location.reload()} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900">
                Recharger
              </Button>
            </>
          )}
        </div>
      </ThemeWrapper>
    );
  }

  return (
    <ThemeWrapper className="flex flex-col min-h-screen p-4 md:p-8">
      {!showIntro && (
        <>
          {/* Progress bar */}
          <div className="w-full max-w-2xl mx-auto mb-6 z-10">
            <div className="h-3 md:h-4 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                className="h-full progress-glow rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-center mt-2 text-sm text-white/50">
              {currentIndex + 1} / {questions.length}
            </p>
          </div>
        </>
      )}
      {showIntro && (
        <div className="w-full max-w-2xl mx-auto mb-6 z-10 invisible pointer-events-none select-none" aria-hidden="true">
          <div className="h-3 md:h-4 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full w-0 rounded-full" />
          </div>
          <p className="text-center mt-2 text-sm text-white/50">
            {questions.length > 0 ? `1 / ${questions.length}` : '—'}
          </p>
        </div>
      )}

      <div className="relative z-10 w-full flex flex-col items-stretch pt-1 pb-16">
        <div className="w-full max-w-2xl mx-auto px-5 sm:px-10">
        {showIntro ? (
          <motion.div
            key="intro-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            onAnimationComplete={() => setIntroMotionComplete(true)}
            className="w-full flex flex-col items-stretch gap-4 md:gap-6 text-center"
          >
            <div className="flex justify-center shrink-0 min-h-[230px] items-center">
              <Robot isSpeaking={isSpeaking} speechSpectrum={speechSpectrum} size="large" />
            </div>
            <h2 className="font-bold text-2xl md:text-4xl text-center leading-tight text-glow m-0">
              {introTitle}
            </h2>
            <p className="text-lg md:text-2xl font-medium leading-relaxed text-white/85 max-w-2xl">
              {introText}
            </p>
            <Button
              {...glowPointerHandlers}
              type="button"
              onPointerDown={() => primeSpeechSynthesisFromUserInteraction()}
              onClick={() => {
                stop();
                setShowIntro(false);
              }}
              className="btn-glow btn-radial-cyan-purple text-white font-bold text-xl md:text-2xl py-6 px-10 rounded-full"
            >
              {settings?.ui_texts?.intro_button || settings?.ui_texts?.continue_button || "Continuer"}
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </motion.div>
        ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={
              currentQuestion.type === 'identity'
                ? `${currentIndex}-identity-${identityStep}`
                : String(currentIndex)
            }
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="w-full flex flex-col items-stretch gap-4 md:gap-6"
          >
            <div className="flex justify-center shrink-0 min-h-[230px] items-center">
              <Robot isSpeaking={isSpeaking} speechSpectrum={speechSpectrum} size="large" />
            </div>

            {!(
              currentQuestion.type === 'identity' && identityStep === 2
            ) && (
            <h2 
              data-testid="question-text"
              className="font-bold text-2xl md:text-4xl text-center leading-tight text-glow m-0 mb-2 md:mb-4"
            >
              {currentQuestion.text}
            </h2>
            )}

            {/* Identity Question Type */}
            {currentQuestion.type === 'identity' && (
              <>
                {identityStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-8"
                  >
                    <Input
                      data-testid="name-input"
                      type="text"
                      value={childName}
                      onChange={(e) => setChildName(e.target.value)}
                      placeholder={settings?.ui_texts?.name_placeholder || "Ton prénom..."}
                      className="text-2xl md:text-3xl py-6 md:py-8 px-6 rounded-2xl bg-white/10 border-2 border-white/20 focus:border-cyan-400 text-center placeholder:text-white/40 text-white disabled:opacity-40"
                      maxLength={20}
                      disabled={!hasReadAllChoices}
                      onKeyDown={(e) => e.key === 'Enter' && hasReadAllChoices && handleNameSubmit()}
                    />
                    <Button
                      data-testid="submit-name-btn"
                      {...glowPointerHandlers}
                      onClick={handleNameSubmit}
                      disabled={!hasReadAllChoices || !childName.trim()}
                      className={`w-full font-bold text-xl md:text-2xl py-6 md:py-8 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                        nameSubmitFlash
                          ? '!bg-emerald-500 hover:!bg-emerald-500 !text-white shadow-lg shadow-emerald-500/40 border-0'
                          : 'btn-glow btn-radial-emerald-cyan text-white'
                      }`}
                    >
                      <Sparkles className="w-6 h-6 mr-2" />
                      {settings?.ui_texts?.name_button || "C'est moi !"}
                    </Button>
                  </motion.div>
                )}

                {identityStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.99 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col gap-4 md:gap-5 text-center w-full"
                  >
                    <h2
                      data-testid="question-text"
                      className="font-bold text-2xl md:text-4xl text-center leading-tight text-glow m-0"
                    >
                      {currentQuestion.text}
                    </h2>

                    <motion.button
                      data-testid="identity-record-btn"
                      {...glowPointerHandlers}
                      onClick={handleRecordToggle}
                      whileTap={{ scale: 0.95 }}
                      disabled={!hasReadAllChoices && !isRecording}
                      className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mx-auto transition-all disabled:opacity-40 disabled:pointer-events-none ${
                        isRecording 
                          ? 'bg-red-500 recording-pulse-modern' 
                          : audioRecorded 
                            ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' 
                            : 'bg-emerald-500 hover:bg-emerald-400 shadow-lg shadow-emerald-500/40'
                      }`}
                    >
                      {isRecording ? (
                        <MicOff className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      ) : audioRecorded ? (
                        <Check className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      ) : (
                        <Mic className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      )}
                    </motion.button>

                    {isRecording && (
                      <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-lg text-red-400 font-medium"
                      >
                        {settings?.ui_texts?.voice_listening || "Je t'écoute..."}
                      </motion.p>
                    )}

                    {audioRecorded && audioUrl && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-lg text-emerald-400 font-medium"
                      >
                        {settings?.ui_texts?.voice_recorded || "J'ai bien entendu !"}
                      </motion.p>
                    )}

                    <div className="flex flex-col gap-4 pt-2">
                      {!audioRecorded && (
                        <button
                          data-testid="identity-skip-btn"
                          type="button"
                          disabled={!hasReadAllChoices}
                          onClick={handleSkipIdentityVoice}
                          className="text-sm text-white/40 hover:text-white/70 underline transition-colors disabled:opacity-30 disabled:pointer-events-none"
                        >
                          {settings?.ui_texts?.skip_link || "Passer cette étape"}
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </>
            )}

            {/* Choices */}
            {currentQuestion.type === 'multiple_choice' && !showAudioStep && (
              <div
                className={`grid grid-cols-2 gap-5 md:gap-7 mt-2 md:mt-3 ${
                  choicesInteractionLocked ? 'pointer-events-none' : ''
                }`}
              >
                {currentQuestion.choices.map((choice, index) => {
                  const label = choiceLabel(choice);
                  const isBeingRead = currentReadingIndex === index;
                  const isHovered = hoverIndex === index;
                  const isDisabled = choicesInteractionLocked;
                  const isSelected = selectedChoice === label;
                  
                  return (
                    <motion.button
                      key={`${index}-${label}`}
                      data-testid={`choice-btn-${index}`}
                      onClick={() => handleChoiceSelect(label)}
                      onMouseEnter={() => handleHoverStart(index, label)}
                      onMouseLeave={handleHoverEnd}
                      disabled={isDisabled}
                      animate={{
                        scale: isBeingRead ? 1.08 : isSelected ? 1.05 : 1,
                        opacity:
                          choicesInteractionLocked && !isBeingRead && !isSelected ? 0.45 : 1,
                      }}
                      whileHover={choicesInteractionLocked ? undefined : { scale: isSelected ? 1.07 : 1.04 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative ${choiceRadialClasses[index % choiceRadialClasses.length]} ${choiceHoverHalo} text-white font-bold text-lg md:text-xl py-5 md:py-6 px-4 rounded-2xl transition-all duration-300 ${
                        isBeingRead ? 'shadow-[0_0_18px_rgba(255,255,255,0.45)]' : ''
                      } ${isSelected ? 'choice-selected-shiny choice-selected-glow' : ''}`}
                    >
                      {/* Circular hover timer (desktop) */}
                      {isHovered && hoverProgress > 0 && hoverProgress < 100 && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 hidden md:block">
                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              fill="rgba(0,0,0,0.5)"
                              stroke="none"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="8"
                              fill="none"
                              stroke="white"
                              strokeWidth="3"
                              strokeDasharray={`${hoverProgress * 0.5} 50`}
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      )}
                      
                      {/* Speaker icon for mobile - click to hear */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleSpeakChoice(e, index, label)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSpeakChoice(e, index, label)}
                        className="absolute top-2 right-2 p-1.5 bg-white/20 rounded-full md:hidden active:bg-white/40 cursor-pointer"
                        aria-label="Écouter"
                      >
                        <Volume2 className="w-4 h-4" />
                      </span>
                      
                      {label}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Open question */}
            {currentQuestion.type === 'open_voice' && !showAudioStep && (
              <div className="space-y-6 text-center">
                <motion.button
                  data-testid="open-record-btn"
                  {...glowPointerHandlers}
                  onClick={handleRecordToggle}
                  whileTap={{ scale: 0.95 }}
                  disabled={!hasReadAllChoices && !isRecording}
                  className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mx-auto transition-all disabled:opacity-40 disabled:pointer-events-none ${
                    isRecording 
                      ? 'bg-red-500 recording-pulse-modern' 
                      : audioRecorded 
                        ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' 
                        : 'bg-emerald-500 hover:bg-emerald-400 shadow-lg shadow-emerald-500/40'
                  }`}
                >
                  {isRecording ? (
                    <MicOff className="w-12 h-12 md:w-16 md:h-16 text-white" />
                  ) : audioRecorded ? (
                    <Check className="w-12 h-12 md:w-16 md:h-16 text-white" />
                  ) : (
                    <Mic className="w-12 h-12 md:w-16 md:h-16 text-white" />
                  )}
                </motion.button>

                {isRecording && (
                  <p className="text-lg text-red-400 animate-pulse">{settings?.ui_texts?.voice_listening || "Je t'écoute..."}</p>
                )}
              </div>
            )}

            {/* Audio follow-up step */}
            {showAudioStep && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8 text-center glass-card p-6 rounded-3xl"
              >
                {mcFollowupDisplayText ? (
                  <p className="font-bold text-xl md:text-2xl text-cyan-400">{mcFollowupDisplayText}</p>
                ) : null}

                {currentQuestion.type === 'multiple_choice' && (
                  <div className="flex flex-col items-center gap-3">
                    <motion.button
                      data-testid="audio-step-record-btn"
                      {...glowPointerHandlers}
                      onClick={handleRecordToggle}
                      whileTap={{ scale: 0.95 }}
                      disabled={!hasReadAllChoices && !isRecording}
                      className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mx-auto transition-all disabled:opacity-40 disabled:pointer-events-none ${
                        isRecording 
                          ? 'bg-red-500 recording-pulse-modern' 
                          : audioRecorded 
                            ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' 
                            : 'bg-emerald-500 hover:bg-emerald-400 shadow-lg shadow-emerald-500/40'
                      }`}
                    >
                      {isRecording ? (
                        <MicOff className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      ) : audioRecorded ? (
                        <Check className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      ) : (
                        <Mic className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      )}
                    </motion.button>
                    {isRecording && (
                      <p className="text-base md:text-lg text-red-400 font-medium max-w-md">
                        {settings?.ui_texts?.choice_voice_stop_sends ||
                          'Quand tu as fini de me parler, touche le bouton rouge.'}
                      </p>
                    )}
                    {hasReadAllChoices && !isRecording && !audioRecorded && (
                      <p className="text-sm md:text-base text-white/60 max-w-md">
                        {settings?.ui_texts?.choice_voice_record_then_stop ||
                          'Touche le micro pour me parler, puis le bouton rouge quand tu as terminé.'}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-5 pt-2">
                  <button
                    data-testid="skip-extra-btn"
                    type="button"
                    disabled={!hasReadAllChoices}
                    onClick={handleSkipAudioAndContinue}
                    className="text-sm text-white/40 hover:text-white/70 underline disabled:opacity-30 disabled:pointer-events-none"
                  >
                    {settings?.ui_texts?.skip_question || 'Passer'}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
        )}
        </div>
      </div>
    </ThemeWrapper>
  );
};

export default QuestionnairePage;
